import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Icon from '../AppIcon';
import Button from './Button';
import LanguageSelector from '../LanguageSelector';
import { remindersService } from '../../services/reminders';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [unreadReminders, setUnreadReminders] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation('header');

  const navigationItems = [
    { name: t('navigation.home'), path: '/homepage', icon: 'Home' },
    { name: t('navigation.giftIdeas'), path: '/gift-ideas-hub', icon: 'BookOpen' },
    { name: t('navigation.wishlists'), path: '/wishlists', icon: 'Heart' },
    { name: t('navigation.occasions'), path: '/occasions', icon: 'Calendar' },
    { name: t('navigation.giveaways'), path: '/giveaways', icon: 'Gift' }
  ];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Load unread reminders count
  useEffect(() => {
    const loadUnreadReminders = async () => {
      try {
        const data = await remindersService.getUnreadReminders();
        setUnreadReminders(data.count || data.length || 0);
      } catch (error) {
        console.error('Error loading reminders:', error);
      }
    };

    loadUnreadReminders();
    // Refresh every 5 minutes
    const interval = setInterval(loadUnreadReminders, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const handleNavigation = (path) => {
    navigate(path);
    setIsMenuOpen(false);
  };

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const isActivePath = (path) => {
    return location.pathname === path;
  };

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled
      ? 'bg-white/95 backdrop-blur-md shadow-soft border-b border-accent-100'
      : 'bg-white/90 backdrop-blur-sm'
      }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-20">
          {/* Logo */}
          <div
            className="flex items-center space-x-3 cursor-pointer group"
            onClick={() => handleNavigation('/homepage')}
          >
            <div className="relative">
              <img
                src="/static/frontend/assets/images/Giftsy-Logo-Final-1751671984839.png"
                alt="Giftsy Logo"
                className="w-10 h-10 lg:w-12 lg:h-12 object-contain transition-all duration-300 group-hover:scale-105"
              />
            </div>
            <div className="hidden sm:block">
              <h1 className="font-heading font-bold text-xl lg:text-2xl text-accent-800 group-hover:text-primary transition-colors duration-300">
                {t('brand.title')}
              </h1>
            </div>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-1">
            {navigationItems.map((item) => (
              <button
                key={item.path}
                onClick={() => handleNavigation(item.path)}
                className={`flex items-center space-x-2 px-4 py-2 rounded-xl font-medium text-sm transition-all duration-300 hover:bg-surface group ${isActivePath(item.path)
                  ? 'text-primary bg-primary-50 shadow-sm'
                  : 'text-accent-600 hover:text-accent-800'
                  }`}
              >
                <Icon
                  name={item.icon}
                  size={18}
                  className={`transition-colors duration-300 ${isActivePath(item.path) ? 'text-primary' : 'text-accent-500 group-hover:text-accent-700'
                    }`}
                />
                <span>{item.name}</span>
              </button>
            ))}
          </nav>

          {/* Desktop Actions */}
          <div className="hidden lg:flex items-center space-x-3">
            <LanguageSelector variant="compact" size="sm" />

            {/* Notifications Bell */}
            <div className="relative">
              <Button
                variant="ghost"
                size="sm"
                iconName="Bell"
                onClick={() => setShowNotifications(!showNotifications)}
                className="text-accent-600 hover:text-accent-800 hover:bg-surface relative"
              >
                {unreadReminders > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                    {unreadReminders > 9 ? '9+' : unreadReminders}
                  </span>
                )}
              </Button>

              {/* Notifications Dropdown */}
              {showNotifications && (
                <div className="absolute right-0 top-full mt-2 w-80 bg-white rounded-xl shadow-lg border border-accent-100 z-50">
                  <div className="p-4 border-b border-accent-100">
                    <h3 className="font-semibold text-accent-800">Notifications</h3>
                  </div>
                  <div className="p-4">
                    {unreadReminders > 0 ? (
                      <p className="text-sm text-accent-600">You have {unreadReminders} unread reminder{unreadReminders !== 1 ? 's' : ''}</p>
                    ) : (
                      <p className="text-sm text-accent-600">No new notifications</p>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        handleNavigation('/occasions');
                        setShowNotifications(false);
                      }}
                      className="mt-2 w-full"
                    >
                      View All Reminders
                    </Button>
                  </div>
                </div>
              )}
            </div>

            <Button
              variant="ghost"
              size="sm"
              iconName="User"
              onClick={() => handleNavigation('/profile-dashboard')}
              className="text-accent-600 hover:text-accent-800 hover:bg-surface"
            >
              {t('actions.profile')}
            </Button>
            <Button
              variant="primary"
              size="sm"
              iconName="Sparkles"
              iconPosition="left"
              onClick={() => handleNavigation('/gift-finder')}
              className="shadow-warm hover:shadow-primary"
            >
              {t('actions.findGifts')}
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <div className="lg:hidden flex items-center space-x-2">
            <LanguageSelector variant="compact" size="sm" />
            <Button
              variant="ghost"
              size="sm"
              iconName="User"
              onClick={() => handleNavigation('/profile-dashboard')}
              className="text-accent-600"
            />
            <button
              onClick={toggleMenu}
              className="p-2 rounded-lg text-accent-600 hover:text-accent-800 hover:bg-surface transition-colors duration-200"
              aria-label={t('actions.toggleMenu')}
            >
              <Icon name={isMenuOpen ? "X" : "Menu"} size={24} />
            </button>
          </div>
        </div>

        {/* Mobile Navigation Menu */}
        {isMenuOpen && (
          <div className="lg:hidden absolute top-full left-0 right-0 bg-white/95 backdrop-blur-md border-b border-accent-100 shadow-lg animate-slide-down">
            <div className="px-4 py-4 space-y-2">
              {navigationItems.map((item) => (
                <button
                  key={item.path}
                  onClick={() => handleNavigation(item.path)}
                  className={`w-full flex items-center space-x-3 px-4 py-3 rounded-xl font-medium text-left transition-all duration-200 ${isActivePath(item.path)
                    ? 'text-primary bg-primary-50 shadow-sm'
                    : 'text-accent-600 hover:text-accent-800 hover:bg-surface'
                    }`}
                >
                  <Icon
                    name={item.icon}
                    size={20}
                    className={isActivePath(item.path) ? 'text-primary' : 'text-accent-500'}
                  />
                  <span>{item.name}</span>
                </button>
              ))}
              <div className="pt-4 border-t border-accent-100 mt-4">
                <Button
                  variant="primary"
                  fullWidth
                  iconName="Sparkles"
                  iconPosition="left"
                  onClick={() => handleNavigation('/gift-finder')}
                  className="shadow-warm"
                >
                  {t('actions.findGifts')}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;